import React, {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useState,
} from 'react';
import { AutoCreateEventFormValues } from './screens/Form/types';
import { TransientAutoCreatedEvent } from './shared';

export const TRANSIENT_AUTO_CREATED_EVENT_KEY = 'transient_auto_created_event';
export const AUTO_CREATE_EVENT_FORM_VALUES_KEY =
    'auto_create_event_form_values';

interface DraftEventStore {
    loadedTransientAutoCreatedEvent: boolean;
    loadedSavedAutoCreateFormValues: boolean;
    transientAutoCreatedEvent: TransientAutoCreatedEvent | null;
    savedAutoCreateFormValues: AutoCreateEventFormValues | null;
    setSavedAutoCreateFormValues: Function;
    setTransientAutoCreatedEvent: Function;
}

const initialValue = {} as DraftEventStore;
const DraftEventContext = createContext<DraftEventStore>(initialValue);

const initializeTransientAutoCreatedEvent = (): TransientAutoCreatedEvent => {
    const rawEvent = window.sessionStorage?.getItem(
        TRANSIENT_AUTO_CREATED_EVENT_KEY,
    );
    return rawEvent ? JSON.parse(rawEvent) : null;
};

const initializeSavedAutoCreateFormValues = (): AutoCreateEventFormValues => {
    const rawValues = window.sessionStorage?.getItem(
        AUTO_CREATE_EVENT_FORM_VALUES_KEY,
    );
    return rawValues ? JSON.parse(rawValues) : null;
};

export const DraftEventProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    const [
        loadedTransientAutoCreatedEvent,
        setLoadedTransientAutoCreatedEvent,
    ] = useState<boolean>(false);
    const [
        loadedSavedAutoCreateFormValues,
        setLoadedSavedAutoCreateFormValues,
    ] = useState<boolean>(false);
    const [transientAutoCreatedEvent, setTransientAutoCreatedEvent] =
        useState<TransientAutoCreatedEvent | null>(null);
    const [savedAutoCreateFormValues, setSavedAutoCreateFormValues] =
        useState<AutoCreateEventFormValues | null>(null);

    useEffect(() => {
        setTransientAutoCreatedEvent(initializeTransientAutoCreatedEvent());
        setSavedAutoCreateFormValues(initializeSavedAutoCreateFormValues());
        setLoadedTransientAutoCreatedEvent(true);
        setLoadedSavedAutoCreateFormValues(true);
    }, []);

    return (
        <DraftEventContext.Provider
            value={{
                savedAutoCreateFormValues,
                setSavedAutoCreateFormValues,
                transientAutoCreatedEvent,
                setTransientAutoCreatedEvent,
                loadedTransientAutoCreatedEvent,
                loadedSavedAutoCreateFormValues,
            }}
        >
            {children}
        </DraftEventContext.Provider>
    );
};

export const useTransientAutoCreatedEventContext = () => {
    const { transientAutoCreatedEvent, setTransientAutoCreatedEvent } =
        useContext(DraftEventContext);

    if (
        transientAutoCreatedEvent === undefined ||
        setTransientAutoCreatedEvent === undefined
    ) {
        throw new Error(
            'useTransientAutoCreatedEventContext must be used within a DraftEventProvider',
        );
    }

    const saveTransientAutoCreatedEvent = (data: Record<string, any>) => {
        const updated = { ...transientAutoCreatedEvent, ...data };
        setTransientAutoCreatedEvent(updated);

        window?.sessionStorage?.setItem(
            TRANSIENT_AUTO_CREATED_EVENT_KEY,
            JSON.stringify(updated),
        );
    };

    return {
        transientAutoCreatedEvent,
        saveTransientAutoCreatedEvent,
    };
};

export const useSavedAutoCreatedEventFormValuesContext = () => {
    const { savedAutoCreateFormValues, setSavedAutoCreateFormValues } =
        useContext(DraftEventContext);

    if (
        savedAutoCreateFormValues === undefined ||
        setSavedAutoCreateFormValues === undefined
    ) {
        throw new Error(
            'useSavedAutoCreatedEventFormValuesContext must be used within a DraftEventProvider',
        );
    }

    const saveAutoCreatedEventFormValues = (data: Record<string, any>) => {
        const updated = { ...savedAutoCreateFormValues, ...data };
        setSavedAutoCreateFormValues(updated);

        window?.sessionStorage?.setItem(
            AUTO_CREATE_EVENT_FORM_VALUES_KEY,
            JSON.stringify(updated),
        );
    };

    return {
        savedAutoCreateFormValues,
        saveAutoCreatedEventFormValues,
    };
};

export const useLoadedTransientEvent = () => {
    const { loadedTransientAutoCreatedEvent } = useContext(DraftEventContext);

    if (loadedTransientAutoCreatedEvent === undefined) {
        throw new Error(
            'useLoadedTransientEvent must be used within a DraftEventProvider',
        );
    }

    return {
        loadedTransientAutoCreatedEvent,
    };
};

export const useLoadedSavedAutoCreateFormValues = () => {
    const { loadedSavedAutoCreateFormValues } = useContext(DraftEventContext);

    if (loadedSavedAutoCreateFormValues === undefined) {
        throw new Error(
            'useLoadedSavedAutoCreateFormValues must be used within a DraftEventProvider',
        );
    }

    return {
        loadedSavedAutoCreateFormValues,
    };
};
